/**
 * Gate a route on a live session.
 *
 * Three outcomes, not two, and the third is the one that matters: `refused` renders a
 * terminal message **in place** rather than redirecting. Bouncing a `principal_unbound`
 * user to the login screen would send them through a sign-in that succeeds and lands them
 * right back here — a loop with no error message and no way out (ADR-0016).
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from './session.js';

export function RequireSession({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { state } = useSession();
  const { t } = useTranslation();

  // Never redirect on the first paint: the probe has not answered yet, and flashing the
  // login screen at an already-signed-in user is the classic version of this bug.
  if (state.status === 'loading') {
    return <p className="muted">{t('session.checking')}</p>;
  }

  if (state.status === 'anonymous') {
    return <Navigate to="/login" replace />;
  }

  if (state.status === 'unreachable') {
    return (
      <section className="card">
        <h2>{t('session.unreachableTitle')}</h2>
        <p className="muted">{t('status.unreachable')}</p>
      </section>
    );
  }

  if (state.status === 'refused') {
    return (
      <section className="card">
        <h2>{t('session.refusedTitle')}</h2>
        {/* The API's own code, translated. Falls back to Arabic, never to the raw code. */}
        <p className="muted">{t(`error.${state.code}`, { defaultValue: t('error.unexpected') })}</p>
        <p className="muted">{t('session.refusedHelp')}</p>
      </section>
    );
  }

  return <>{children}</>;
}
